import React from 'react';
import { useDispatch } from 'react-redux';
import { iceServers } from './ice-servers';
import { ChannelRequestEnum } from '@src/core/socket';
import { setCameraStatus } from '@src/features/order/pages/checklist-page/redux';
import { useConnectionState } from './useConnectionState';
import { useSignaling } from './useSignaling';
import { useReconnect } from './useReconnect';

const CONNECTION_TIMEOUT = 15000;

const useCamera = (serialNumber: string, isViewer: boolean) => {
  const dispatch = useDispatch();

  const {
    isLoading,
    isConnected,
    isReconnecting,
    reconnectAttempts,
    isLoadingRef,
    isConnectedRef,
    setIsLoading,
    setIsConnected,
    setIsReconnecting,
    setReconnectAttempts,
    isReconnectingRef,
    reconnectAttemptsRef,
  } = useConnectionState();

  const [viewerId] = React.useState(() => 'viewer_' + Math.random().toString(36).substr(2, 9));

  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const peerConnections = React.useRef<RTCPeerConnection | null>(null);
  const connectionTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const { arcSocket, readyState, sendSignal } = useSignaling({
    serialNumber,
    isViewer,
    viewerId,
    peerConnections,
  });

  const updateCameraStatus = React.useCallback((connected: boolean) => {
    setIsConnected(connected);
    setIsLoading(false);
    dispatch(setCameraStatus({ serialNumber, connected }));
  }, [dispatch, serialNumber, setIsConnected, setIsLoading]);

  const clearConnectionTimeout = React.useCallback(() => {
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
  }, []);

  const initWebRTC = async () => {
    try {
      const webSocket = arcSocket();
      if (!webSocket || webSocket.readyState !== WebSocket.OPEN) {
        console.warn('WebSocket not ready, will retry when connected');
        setIsLoading(false);
        return;
      }

      clearConnectionTimeout();

      connectionTimeoutRef.current = setTimeout(() => {
        if (isLoadingRef.current && !isConnectedRef.current) {
          console.warn('Camera connection timeout');
          setIsLoading(false);
        }
      }, CONNECTION_TIMEOUT);

      peerConnections.current = new RTCPeerConnection({
        iceServers,
        iceCandidatePoolSize: 10,
        rtcpMuxPolicy: 'require',
        bundlePolicy: 'max-bundle',
      });

      const offer = await peerConnections.current.createOffer({
        offerToReceiveVideo: true,
        offerToReceiveAudio: true,
      });
      await peerConnections.current.setLocalDescription(offer);

      peerConnections.current.ontrack = (event: RTCTrackEvent) => {
        if (videoRef.current) {
          videoRef.current.srcObject = event.streams[0];
        }
      };

      sendSignal(isViewer ? 'sfu_offer' : 'offer', offer.sdp);

      peerConnections.current.onicecandidate = (event) => {
        if (event.candidate) {
          sendSignal(
            isViewer ? 'sfu_ice_candidate' : 'ice_candidate',
            JSON.stringify(event.candidate),
          );
        }
      };

      peerConnections.current.oniceconnectionstatechange = () => {
        const state = peerConnections.current?.iceConnectionState;
        if (state === 'failed') {
          console.error('ICE connection failed. Checking network and TURN/STUN servers.');
          updateCameraStatus(false);
          clearConnectionTimeout();
        } else if (state === 'connected' || state === 'completed') {
          updateCameraStatus(true);
          clearConnectionTimeout();
        } else if (state === 'disconnected' || state === 'closed') {
          updateCameraStatus(false);
        }
      };
    } catch (e) {
      console.error('Error initializing WebRTC:', e);
      setIsLoading(false);
    }
  };

  const onStopVideo = () => {
    const webSocket = arcSocket();
    if (webSocket && webSocket.readyState === WebSocket.OPEN) {
      const payload: any = {
        type: ChannelRequestEnum.WEBRTC,
        command: 'stop',
        data: null,
        serialNumber,
      };
      if (isViewer) {
        payload.viewerMode = 'sfu';
        payload.viewerId = viewerId;
      }
      webSocket.send(JSON.stringify(payload));
    }

    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }

    peerConnections.current?.getTransceivers().forEach(transceiver => transceiver.stop());
    peerConnections.current?.close();
    peerConnections.current = null;

    setIsConnected(false);
    dispatch(setCameraStatus({ serialNumber, connected: false }));
  };

  const onStartVideo = async (): Promise<void> => {
    setIsLoading(true);
    await initWebRTC();
  };

  const { reconnect, clearReconnectTimeout } = useReconnect({
    isReconnectingRef,
    reconnectAttemptsRef,
    setIsReconnecting,
    setReconnectAttempts,
    onStop: onStopVideo,
    onStart: initWebRTC,
  });

  // Lifecycle: connect/disconnect on serialNumber or readyState change
  React.useEffect(() => {
    if (!serialNumber) return;

    if (!readyState) {
      console.log('Waiting for WebSocket to be ready...');
      return;
    }

    onStopVideo();
    onStartVideo();

    return () => {
      onStopVideo();
      clearReconnectTimeout();
      clearConnectionTimeout();
    };
  }, [serialNumber, readyState]);

  // Video element event listeners
  React.useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const handleWaiting = () => setIsLoading(true);
    const handlePlaying = () => updateCameraStatus(true);
    const handleCanPlay = () => updateCameraStatus(true);
    const handleError = () => {
      updateCameraStatus(false);
      reconnect();
    };

    videoElement.addEventListener('waiting', handleWaiting);
    videoElement.addEventListener('playing', handlePlaying);
    videoElement.addEventListener('canplay', handleCanPlay);
    videoElement.addEventListener('error', handleError);

    return () => {
      videoElement.removeEventListener('waiting', handleWaiting);
      videoElement.removeEventListener('playing', handlePlaying);
      videoElement.removeEventListener('canplay', handleCanPlay);
      videoElement.removeEventListener('error', handleError);
    };
  }, [serialNumber, updateCameraStatus, reconnect]);

  return {
    onStopVideo,
    onStartVideo,
    isLoading,
    videoRef,
    reconnect,
    isReconnecting,
    reconnectAttempts,
  };
};

export default useCamera;
